// packages/slide-render/src/kpi-footnotes.ts
//
// KPI definition footnotes shown at the bottom of every workflow-generated
// slide that mentions a tracked KPI. Clients see KPI names on slides but
// often don't know the underlying definition / calculation — the footnote
// closes that gap on the slide itself.
//
// Definitions are copied verbatim from the DashONE glossary
// (`apps/web/src/data/glossaryTerms.js`) so the glossary page and slide
// footers never disagree. Phrasing is intentionally client-agnostic
// ("my products", "my core brands") — no brand names, no category words.
//
// Mirror file — keep in sync when the glossary or this file changes:
//   - firebase/functions/src/shared/kpi-footnotes.ts
// (apps/web consumes this copy via the @dashone/slide-render package.)
// The two copies exist because each runtime (Vite frontend, Firebase
// Node) has a different module resolver and can't import across the
// other. A Vitest at
// `firebase/functions/src/shared/__tests__/kpi-footnotes-mirrors.test.ts`
// reads both copies and asserts `KPI_FOOTNOTES` deep-equals across
// them, so drift between mirrors fails CI.
//
// Regex performance: every regex in this module is linear-time. The
// per-term `\b<term>\b` and `KPI_PROXIMITY_PATTERN` use bounded character
// classes ({0,20}) with no nested or overlapping quantifiers, so they
// can't catastrophically backtrack regardless of input length. Slide text
// is only ever a few hundred chars in practice; even multi-KB inputs
// would scan in time linear to length.

export type KpiCluster = 'availability' | 'bundles' | 'content';
export type KpiSlideForm = 'percentage' | 'count';

export interface KpiFootnote {
  id: string;
  /**
   * Label rendered at the start of the footnote line, e.g. "Fixed Combo
   * Presence". Echoes what the slide says — display labels do not always
   * match the glossary header (slides say "Name Accuracy" where the glossary
   * says "Name Completeness").
   */
  displayLabel: string;
  /**
   * Terms scanned against slide text, longest-first. Lowercase. Whole-word
   * match. Per cluster, the KPI whose longest-matched term is longest wins
   * — so "Fixed Combo Presence" suppresses "Combo Presence" on the same
   * slide.
   */
  matchTerms: string[];
  /**
   * Two definitions per KPI — the footnote picks based on whether the slide
   * shows the metric as a percentage or as a raw count (`detectSlideForm`).
   * Both phrasings come from `glossaryTerms.js`: percentage form from
   * "KPIs (ONE Metrics)", count form from "KPIs (Scorecard)".
   */
  definitions: Record<KpiSlideForm, string>;
  cluster: KpiCluster;
}

export const KPI_FOOTNOTES: readonly KpiFootnote[] = [
  {
    id: 'core-brand-availability',
    displayLabel: 'Core Brand Availability',
    matchTerms: ['core brand availability'],
    definitions: {
      percentage: 'The percentage of digital storefronts listing at least one of my core brands.',
      count: 'The number of digital storefronts listing at least one of my core brands.',
    },
    cluster: 'availability',
  },
  {
    id: 'availability',
    displayLabel: 'Availability',
    matchTerms: ['availability'],
    definitions: {
      percentage: 'The percentage of digital storefronts listing my products.',
      count: 'The number of digital storefronts listing my products.',
    },
    cluster: 'availability',
  },
  {
    id: 'fixed-combo-presence',
    displayLabel: 'Fixed Combo Presence',
    matchTerms: ['fixed combo presence', 'fixed combo'],
    definitions: {
      percentage:
        'The percentage of digital storefronts listing at least one of my products in a fixed combo (a combo where my product cannot be changed).',
      count:
        'The number of digital storefronts listing at least one of my products in a fixed combo (a combo where my product cannot be changed).',
    },
    cluster: 'bundles',
  },
  {
    id: 'option-combo-presence',
    displayLabel: 'Option Combo Presence',
    matchTerms: ['option combo presence', 'option combo'],
    definitions: {
      percentage:
        'The percentage of digital storefronts listing at least one of my products in an option combo (a combo where the user can select among multiple products).',
      count:
        'The number of digital storefronts listing at least one of my products in an option combo (a combo where the user can select among multiple products).',
    },
    cluster: 'bundles',
  },
  {
    id: 'combo-presence',
    displayLabel: 'Combo Presence',
    // Bare `'combo'` is a deliberate over-match: chart titles often say
    // just "Combo by Platform" or "Combo Rate", and we want the footnote
    // to surface even when the full phrase isn't spelled out. Adjacent
    // unrelated uses ("combo deal", "Brand X Combo") will also trip — that's
    // accepted noise. Test: see "bare 'Combo' still surfaces Combo Presence"
    // in kpi-footnotes.test.ts. Fixed Combo / Option Combo win via
    // most-specific resolution within the cluster.
    matchTerms: ['combo presence', 'combo'],
    definitions: {
      percentage: 'The percentage of digital storefronts listing at least one of my products in a combo.',
      count: 'The number of digital storefronts listing at least one of my products in a combo.',
    },
    cluster: 'bundles',
  },
  // Name Accuracy / Image Presence are the registry's outliers: their
  // percentage and count forms are NOT scale variants of one metric — they
  // are two distinct metrics that share a display label, computed off
  // different ClickHouse columns:
  //   percentage → `avg_pct_name_accuracy` / `avg_pct_image_availability`
  //                (SKU basis, ONE Metrics surface)
  //   count      → `nof_dsf_w_name_accuracy` / `nof_dsf_w_image_availability`
  //                (DSF basis, Scorecard surface)
  // The basis swap is deliberate and tracks what the chart actually shows.
  // Do not unify by rewriting one form to the other's basis — the upstream
  // CH columns and the glossary both treat these as two separate KPIs.
  {
    id: 'name-accuracy',
    displayLabel: 'Name Accuracy',
    matchTerms: ['name accuracy', 'name completeness'],
    definitions: {
      percentage: 'The percentage of my SKUs sold in the market with perfect name completeness.',
      count: 'The number of digital storefronts listing my products with perfect name completeness.',
    },
    cluster: 'content',
  },
  {
    id: 'image-presence',
    displayLabel: 'Image Presence',
    // Bare `'image'` is a deliberate over-match: chart titles often say
    // just "Image:" or "Image by Brand" without spelling "Image Presence".
    // Adjacent unrelated uses ("brand image", "image of the storefront")
    // will also trip — accepted noise. Test: see "bare 'Image' surfaces
    // Image Presence" in kpi-footnotes.test.ts.
    matchTerms: ['image presence', 'image'],
    definitions: {
      percentage: 'The percentage of my SKUs sold in the market with a complete image.',
      count: 'The number of digital storefronts listing my products with an image.',
    },
    cluster: 'content',
  },
];

const REGEX_SPECIAL = /[.*+?^${}()|[\]\\]/g;

function matchTermRegex(term: string): RegExp {
  return new RegExp(`\\b${term.toLowerCase().replace(REGEX_SPECIAL, '\\$&')}\\b`, 'i');
}

function longestMatchedTerm(text: string, terms: readonly string[]): string | null {
  let best: string | null = null;
  for (const t of terms) {
    if (matchTermRegex(t).test(text) && (best === null || t.length > best.length)) {
      best = t;
    }
  }
  return best;
}

/**
 * Scans `text` for any tracked KPI mentions and returns the footnotes that
 * should render at the bottom of the slide.
 *
 * Resolution rule: within each cluster, the KPI whose longest-matched term
 * is longest wins. So a slide mentioning "Fixed Combo Presence" returns
 * only the Fixed Combo Presence definition — not Combo Presence, even
 * though "combo presence" also matches as a substring.
 *
 * Returns `[]` when nothing matches → caller renders no footer.
 */
export function detectKpiFootnotes(text: string): KpiFootnote[] {
  if (!text) return [];
  const byCluster = new Map<KpiCluster, { kpi: KpiFootnote; matchLength: number }>();
  for (const kpi of KPI_FOOTNOTES) {
    const matched = longestMatchedTerm(text, kpi.matchTerms);
    if (matched === null) continue;
    const current = byCluster.get(kpi.cluster);
    if (!current || matched.length > current.matchLength) {
      byCluster.set(kpi.cluster, { kpi, matchLength: matched.length });
    }
  }
  return Array.from(byCluster.values()).map((entry) => entry.kpi);
}

// Precomputed pattern: a KPI matchTerm followed within 20 chars by a
// percentage value (digit + `%`) or a bare ` %`. The trailing `%` must be
// preceded by either a digit or whitespace, so column headers like
// `Cov%` (where `%` follows a letter) don't false-trigger.
//
// Anchors detection to the FORWARD direction only — so a headline stat
// like `99.5% availability` (where `%` precedes the KPI) doesn't trigger,
// matching user intent that those titles are metadata, not chart form.
const KPI_PROXIMITY_PATTERN: RegExp = (() => {
  const escaped = KPI_FOOTNOTES.flatMap((f) => f.matchTerms)
    .map((t) => t.toLowerCase().replace(REGEX_SPECIAL, '\\$&'))
    .sort((a, b) => b.length - a.length);
  return new RegExp(
    `\\b(${escaped.join('|')})\\b[^.!?\\n]{0,20}(\\d+(\\.\\d+)?\\s*%|\\s%)`,
    'i',
  );
})();

/**
 * Heuristic: decides whether the slide presents its KPIs as percentages or
 * as raw counts, so the footnote phrasing matches what the chart shows.
 *
 * Triggers `'percentage'` if either:
 *   1. A strong global signal exists anywhere on the slide:
 *      `% by`, `% per`, `percentage of`, `(%)`, or whole-word `percentage`.
 *   2. A KPI keyword is followed within 20 chars by a percentage value or
 *      ` %`. This catches chart titles like `Availability %` / `Combo %`
 *      and big-number tiles like `Combo Rate 0%` while ignoring headline
 *      stats like `99.5% availability` (% precedes the KPI, not follows
 *      it) and column headers like `Cov%` (% follows a letter).
 *
 * Otherwise `'count'`. DashAI slides default to DSF-count form unless one
 * of the signals above is present.
 *
 * To force percentage form on a specific slide whose text doesn't match,
 * pass an explicit `slideText="(%)"` (or similar) through `SlideFrameV2`.
 */
export function detectSlideForm(text: string): KpiSlideForm {
  if (!text) return 'count';
  if (/(%\s+(by|per)\b|percentage of\b|\(\s*%\s*\)|\bpercentage\b)/i.test(text)) {
    return 'percentage';
  }
  if (KPI_PROXIMITY_PATTERN.test(text)) return 'percentage';
  return 'count';
}

/**
 * Plain-text rendering of a footnote, suitable for PPTX, PDF, and React.
 * Format: `<displayLabel>: <definition>`
 */
export function formatKpiFootnote(footnote: KpiFootnote, form: KpiSlideForm = 'count'): string {
  return `${footnote.displayLabel}: ${footnote.definitions[form]}`;
}
